import type { FunctionComponent } from 'preact';
import type { TaskDescription } from '@ms-cloudpack/api-server/browser';
import { TaskResultItem } from './TaskResultItem.js';
import { useCloudpack } from '../CloudpackProvider/CloudpackProvider.js';
import { Button } from '../Button/Button.js';
import {
  ResultsAccordion,
  ResultsAccordionContent,
  type ResultsAccordionProps,
} from '../ResultsAccordion/ResultsAccordion.js';
import { Refresh20 } from '../icons/Refresh20.js';

export interface TaskStatusProps extends Pick<ResultsAccordionProps, 'index'> {
  task: TaskDescription;
}

function getCompletion(task: TaskDescription): string {
  if (task.status === 'pending') {
    return 'Running';
  }
  return [
    `Completed in ${task.duration}ms`,
    task.errors?.length && `${task.errors.length} error${task.errors.length > 1 ? 's' : ''}`,
    task.warnings?.length && `${task.warnings.length} warning${task.warnings.length > 1 ? 's' : ''}`,
  ]
    .filter(Boolean)
    .join(', ');
}

/**
 * Item (collapsible section) in the list of bundle task results in the StatusDialog.
 */
export const TaskStatus: FunctionComponent<TaskStatusProps> = (props) => {
  const { task } = props;
  const { name, warnings = [], errors = [], inputPath, outputPath } = task;
  const cloudpack = useCloudpack();

  const openPath = (rootPath: string) => void cloudpack.openFilePath.mutate({ rootPath });

  const openPackageJson = (): void => {
    if (!inputPath) return; // the button won't be shown in this case

    void cloudpack.openCodeEditor.mutate({
      rootPath: inputPath,
      relativePath: 'package.json',
    });
  };

  const restartTask = (): void => {
    if (!inputPath) return;

    void cloudpack.restartTask.mutate({ id: task.id, inputPath });
  };

  // This callback prevents rendering thousands of bundle result details unnecessarily.
  // It could be further optimized with useMemo etc if it's an actual performance issue.
  const getContent = () => (
    <>
      {inputPath ? (
        <ResultsAccordionContent
          itemName="Input path"
          itemValue={
            <>
              <Button variant="link" onClick={() => openPath(inputPath)}>
                {inputPath}
              </Button>{' '}
              (
              <Button variant="link" onClick={openPackageJson}>
                package.json
              </Button>
              )
            </>
          }
        />
      ) : (
        'No input path found for bundle task. This is a Cloudpack bug. Please report it.'
      )}
      {outputPath && (
        <ResultsAccordionContent
          itemName="Output path"
          itemValue={
            <Button variant="link" onClick={() => openPath(outputPath)}>
              {outputPath}
            </Button>
          }
        />
      )}
      {errors.length > 0 && (
        <ResultsAccordionContent title="Errors">
          {errors.map((item, index) => (
            <TaskResultItem type="error" key={index} item={item} inputPath={inputPath} index={index} />
          ))}
        </ResultsAccordionContent>
      )}
      {warnings.length > 0 && (
        <ResultsAccordionContent title="Warnings">
          {warnings.map((item, index) => (
            <TaskResultItem type="warning" key={index} item={item} inputPath={inputPath} index={index} />
          ))}
        </ResultsAccordionContent>
      )}
    </>
  );

  return (
    <ResultsAccordion
      name={name || '<unknown>'}
      index={props.index}
      result={errors.length ? 'error' : warnings.length ? 'warning' : 'success'}
      headerEndContent={getCompletion(task)}
      headerCommands={
        <Button
          variant="iconOnly"
          onClick={restartTask}
          title="Re-bundle this package"
          aria-label="Re-bundle this package"
        >
          <Refresh20 />
        </Button>
      }
      getContent={getContent}
    />
  );
};
